import React from 'react';
import Image from 'next/image';

interface PageErrorFallbackProps {
  error?: Error;
  eventId?: string | null;
  onReset?: () => void;
}

/**
 * PageErrorFallback - Full-page error display for catastrophic errors
 *
 * Used as the fallback for the outer ErrorBoundary that wraps
 * all providers and the authenticated app. Shows when something
 * goes fundamentally wrong (provider initialization, auth errors, etc.)
 */
export function PageErrorFallback({ error, eventId, onReset }: PageErrorFallbackProps) {
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-struvital-light to-struvital-dark flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Image
              src="/struvital-logo.png"
              alt="Struvital"
              width={200}
              height={80}
              priority
              className="h-20 w-auto opacity-80"
            />
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg shadow-xl p-8 border border-white/20">
            <div className="flex justify-center mb-4">
              <svg
                className="h-16 w-16 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-white mb-2">
              Something went wrong
            </h2>
            <p className="text-struvital-light mb-6">
              The dashboard encountered an unexpected error. Our team has been notified.
            </p>

            {eventId && (
              <p className="text-xs text-white/60 mb-6">
                Reference: {eventId}
              </p>
            )}

            {process.env.NODE_ENV === 'development' && error && (
              <details className="mb-6 text-left">
                <summary className="text-sm text-white/70 cursor-pointer hover:text-white">
                  Error details
                </summary>
                <pre className="mt-2 text-xs bg-black/30 p-3 rounded overflow-auto max-h-32 text-red-300">
                  {error.message}
                  {error.stack && `\n\n${error.stack}`}
                </pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {onReset && (
                <button
                  onClick={onReset}
                  className="px-6 py-2 text-sm font-medium text-struvital-dark bg-white hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-struvital-dark"
                >
                  Try Again
                </button>
              )}
              <button
                onClick={handleRefresh}
                className="px-6 py-2 text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-struvital-dark"
              >
                Refresh Page
              </button>
            </div>

            <p className="mt-6 text-xs text-white/50">
              If this problem persists, please contact support
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
